import React, {FC, memo, useState} from "react";
import {Button, Modal, Popconfirm, Space, Tag, Tooltip} from "antd";
import {DeleteFilled, EditOutlined, PlusCircleOutlined, SaveOutlined} from "@ant-design/icons";
import {
    GetBookingByIdDocument,
    GetBookingByIdQuery,
    GetBookingByIdWithCourseLessonBookingsDocument,
    GetBookingByIdWithCourseLessonBookingsQuery,
    GetPaidScheduledPaymentsDocument,
    GetUnpaidScheduledPaymentsDocument,
    PaymentProvider,
    ScheduledPaymentType,
    useUpdateSettledBookingDiscountMutation,
} from "../../generated/graphql";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import MoneyInput from "../MoneyInput/MoneyInput";
import {useCurrency} from "../../helpers/currencyHelper";
import {renderAmount, CurrencyShort} from "../../helpers/renderAmount";
import {erasedRecordMessage} from "../../helpers/erasedRecordMessage";

type PossibleBookingType =
    | GetBookingByIdQuery["bookingById"]
    | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type SettledBookingCreditProps = {
    booking?: PossibleBookingType;
    bookingId: string;
    allPaid: boolean;
    setTimeStamp?: (ts: number) => void;
};

const SettledBookingCredit: FC<SettledBookingCreditProps> = ({booking, bookingId, allPaid, setTimeStamp}) => {
    const currency = useCurrency();
    const [modalOpen, setModalOpen] = useState(false);
    const creditedBalance = booking?.creditedBalance ?? 0;
    const bookerCredit = booking?.attendee?.booker?.credit ?? 0;

    // Im Edit-Modus: Bereits verrechnetes zurueckrechnen
    const availableCredit = bookerCredit + creditedBalance;

    const [editCreditAmount, setEditCreditAmount] = useState(creditedBalance);

    const [updateDiscount, {loading}] = useUpdateSettledBookingDiscountMutation({
        refetchQueries: [
            {query: GetBookingByIdDocument, variables: {id: bookingId}},
            {query: GetBookingByIdWithCourseLessonBookingsDocument, variables: {id: bookingId}},
            {query: GetUnpaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
            {query: GetPaidScheduledPaymentsDocument, variables: {bookingId, options: {}}},
        ],
        awaitRefetchQueries: true,
    });

    // Wenn alle Posten bezahlt -> read-only Anzeige wenn creditedBalance > 0
    if (allPaid) {
        if (creditedBalance <= 0) {
            return null;
        }

        return (
            <Tag color="orange">
                <Space size={5}>
                    <span>{"Verrechnetes Guthaben"}</span>
                    <RenderAmount amount={creditedBalance} style={{fontSize: "unset", fontWeight: 600}} />
                </Space>
            </Tag>
        );
    }

    // Phase 15 / LESSB-04 / D-10 — Defense-in-Depth UI-Disable:
    // Wenn mindestens eine Rate bereits online (Mollie) bezahlt ist, darf kein Guthaben
    // mehr verrechnet werden. Der Backend-Guard in settledBookingDiscount.service.ts wirft
    // ForbiddenError bei Umgehung; dieses UI-Disable ist die benutzerfreundliche erste Schicht.
    const hasPaidMollieSP = (booking?.scheduledPayments ?? []).some(
        (sp) =>
            sp.paymentDone !== null
            && sp.paymentDone !== undefined
            && sp.paymentProvider === PaymentProvider.Mollie,
    );

    if (hasPaidMollieSP) {
        return (
            <Tooltip title='Bezahlte Online-Raten können hier nicht mit Guthaben verrechnet werden — nutze dafür den „Buchung korrigieren"-Wizard auf der Rechnungs-Karte (Erstattung oder Gutschrift).'>
                <Button type="dashed" size="small" icon={<PlusCircleOutlined />} disabled>
                    {"Guthaben verrechnen"}
                </Button>
            </Tooltip>
        );
    }

    const hasPartiallyPaidOpenSP = (booking?.scheduledPayments ?? []).some((sp) => {
        if (sp.paymentDone != null || sp.openAmount == null || sp.paymentAmount == null) {
            return false;
        }
        const openAmount = Number(sp.openAmount);
        const paymentAmount = Number(sp.paymentAmount);

        return openAmount > 0 && openAmount < paymentAmount;
    });
    const partialPaidBlockTitle =
        "Teilbezahlte offene Raten können nicht automatisch mit Guthaben verrechnet werden — bitte die Teilzahlung zuerst abschließen.";

    // Max-Guthaben-Verrechnung: Minimum aus verfuegbarem Guthaben und offenen Posten
    const unpaidStandardSum = (booking?.scheduledPayments ?? [])
        .filter((p) => p.type === ScheduledPaymentType.Standard && p.paymentDone === null)
        .reduce((sum, p) => sum + (p.paymentAmount ?? 0), 0);
    const maxDeductionFromPayments = unpaidStandardSum + creditedBalance - (booking?.discount ?? 0) - (booking?.memberDiscount ?? 0);
    const maxCreditAmount = Math.min(availableCredit, Math.max(0, maxDeductionFromPayments));

    const showCorrectionInvoiceHint = () => {
        Modal.info({
            title: "Hinweis: Korrekturrechnung",
            content: "Die Buchungsbeträge wurden angepasst. Bitte prüfe, ob eine Korrekturrechnung erstellt und dem Bucher zugeschickt werden muss, damit dieser eine aktuelle Rechnung erhält.",
        });
    };

    const handleSave = async () => {
        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    creditedBalance: editCreditAmount,
                },
            });
            setTimeStamp?.(Date.now());
            setModalOpen(false);
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Das Guthaben konnte nicht verrechnet werden. Bitte versuche es erneut.",
            });
        }
    };

    const handleDelete = async () => {
        try {
            await updateDiscount({
                variables: {
                    bookingId,
                    creditedBalance: 0,
                },
            });
            setTimeStamp?.(Date.now());
            showCorrectionInvoiceHint();
        } catch (error) {
            Modal.error({
                title: "Fehler",
                content: erasedRecordMessage(error) ?? "Die Guthaben-Verrechnung konnte nicht entfernt werden.",
            });
        }
    };

    const openEditModal = () => {
        setEditCreditAmount(creditedBalance || 0);
        setModalOpen(true);
    };

    // Guthaben verrechnet -> Badge mit Edit/Delete
    if (creditedBalance > 0) {
        return (
            <>
                {renderCreditModal()}
                <Tag color="orange">
                    <Space size={5}>
                        <span>{"Verrechnetes Guthaben"}</span>
                        <RenderAmount amount={creditedBalance} style={{fontSize: "unset", fontWeight: 600}} />
                        {hasPartiallyPaidOpenSP ? (
                            <Tooltip title={partialPaidBlockTitle}>
                                <EditOutlined style={{color: "#d9d9d9", cursor: "not-allowed"}} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Guthaben-Verrechnung bearbeiten">
                                <EditOutlined style={{cursor: "pointer"}} onClick={openEditModal} />
                            </Tooltip>
                        )}
                        {hasPartiallyPaidOpenSP ? (
                            <Tooltip title={partialPaidBlockTitle}>
                                <DeleteFilled style={{color: "#d9d9d9", cursor: "not-allowed"}} />
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title="Guthaben-Verrechnung wirklich entfernen?"
                                onConfirm={handleDelete}
                                okText="Ja, entfernen"
                                okType="danger"
                                cancelText="Abbrechen"
                            >
                                <Tooltip title="Guthaben-Verrechnung entfernen">
                                    <DeleteFilled style={{cursor: "pointer", color: "#ff4d4f"}} />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Space>
                </Tag>
            </>
        );
    }

    // Kein Guthaben verrechnet, aber Bucher hat Guthaben
    if (availableCredit > 0) {
        if (hasPartiallyPaidOpenSP) {
            return (
                <Tooltip title={partialPaidBlockTitle}>
                    <Button type="dashed" size="small" icon={<PlusCircleOutlined />} disabled>
                        {`Guthaben verrechnen (${renderAmount({amount: availableCredit, currency: currency.short as CurrencyShort, short: true})} verfügbar)`}
                    </Button>
                </Tooltip>
            );
        }

        return (
            <>
                {renderCreditModal()}
                <Button type="dashed" size="small" icon={<PlusCircleOutlined />} onClick={openEditModal}>
                    {`Guthaben verrechnen (${renderAmount({amount: availableCredit, currency: currency.short as CurrencyShort, short: true})} verfügbar)`}
                </Button>
            </>
        );
    }

    // Kein Guthaben verfuegbar
    return null;

    function renderCreditModal() {
        return (
            <Modal
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                title={creditedBalance > 0 ? "Guthaben-Verrechnung bearbeiten" : "Guthaben verrechnen"}
                okText="Speichern"
                onOk={handleSave}
                okButtonProps={{loading, icon: <SaveOutlined />, disabled: editCreditAmount <= 0}}
                cancelButtonProps={{disabled: loading}}
                destroyOnClose
            >
                <div style={{display: "flex", flexDirection: "column", gap: 12}}>
                    <div>
                        <div style={{marginBottom: 8}}>
                            {"Verfügbares Guthaben des Buchers: "}
                            <RenderAmount amount={availableCredit} style={{fontWeight: 600}} />
                        </div>
                    </div>
                    <div>
                        <label>{"Zu verrechnendes Guthaben"}</label>
                        <MoneyInput
                            value={editCreditAmount}
                            onChange={(val) => setEditCreditAmount(val ?? 0)}
                            min={0}
                            max={maxCreditAmount}
                            step={0.01}
                            style={{width: "100%"}}
                        />
                        <div style={{color: "#999", fontSize: 12, marginTop: 4}}>
                            {"Maximal verrechenbar: "}
                            <RenderAmount amount={maxCreditAmount} style={{fontSize: "unset"}} />
                        </div>
                    </div>
                </div>
            </Modal>
        );
    }
};

export default memo(SettledBookingCredit);
